/**
 * Flowstate API Service
 *
 * API calls for interview prep catalog, progress, payments, and access control.
 */

import api from '../../api';

// ==================== Catalog APIs (Public) ====================

/**
 * Get catalog overview with all tracks and categories
 */
export const fetchCatalog = async () => {
  const response = await api.get('/api/interview-prep/catalog/');
  return response.data;
};

/**
 * Get all tracks
 */
export const fetchTracks = async () => {
  const response = await api.get('/api/interview-prep/tracks/');
  return response.data;
};

/**
 * Get track details with categories
 */
export const fetchTrackDetails = async (trackId) => {
  const response = await api.get(`/api/interview-prep/tracks/${trackId}/`);
  return response.data;
};

/**
 * Get all categories
 */
export const fetchCategories = async () => {
  const response = await api.get('/api/interview-prep/categories/');
  return response.data;
};

/**
 * Get category details with topics
 */
export const fetchCategoryDetails = async (categoryId) => {
  const response = await api.get(`/api/interview-prep/categories/${categoryId}/`);
  return response.data;
};

/**
 * Get topic details with problems
 */
export const fetchTopicDetails = async (topicId) => {
  const response = await api.get(`/api/interview-prep/topics/${topicId}/`);
  return response.data;
};

/**
 * Get problem details
 */
export const fetchProblemDetails = async (problemId) => {
  const response = await api.get(`/api/interview-prep/problems/${problemId}/`);
  return response.data;
};

// ==================== Progress APIs (Authenticated) ====================

/**
 * Get user's overall progress across all topics
 */
export const fetchUserProgress = async () => {
  const response = await api.get('/api/interview-prep/progress/');
  return response.data;
};

/**
 * Get user's progress for a specific topic
 */
export const fetchTopicProgress = async (topicId) => {
  const response = await api.get(`/api/interview-prep/progress/${topicId}/`);
  return response.data;
};

/**
 * Mark a problem as complete
 */
export const markProblemComplete = async (topicId, problemId) => {
  const response = await api.post('/api/interview-prep/progress/mark-complete/', {
    topic_id: topicId,
    problem_id: problemId,
  });
  return response.data;
};

// ==================== Access APIs (Authenticated) ====================

/**
 * Get user's purchased access (tracks and categories)
 */
export const fetchUserAccess = async () => {
  const response = await api.get('/api/interview-prep/access/');
  return response.data;
};

/**
 * Check if user has access to a specific topic
 */
export const checkTopicAccess = async (topicId) => {
  const response = await api.get(`/api/interview-prep/access/topic/${topicId}/`);
  return response.data;
};

// ==================== Payment APIs ====================

/**
 * Create a Razorpay order for purchasing a track or category
 * @param {string} contentType - 'track' or 'category'
 * @param {number} objectId - ID of the track or category
 */
export const createPaymentOrder = async (contentType, objectId) => {
  const response = await api.post('/api/payments/create-order/', {
    content_type: contentType,
    object_id: objectId,
  });
  return response.data;
};

/**
 * Verify payment after Razorpay checkout
 */
export const verifyPayment = async (razorpayOrderId, razorpayPaymentId, razorpaySignature) => {
  const response = await api.post('/api/payments/verify/', {
    razorpay_order_id: razorpayOrderId,
    razorpay_payment_id: razorpayPaymentId,
    razorpay_signature: razorpaySignature,
  });
  return response.data;
};

/**
 * Get user's purchase history
 */
export const fetchUserPurchases = async () => {
  const response = await api.get('/api/payments/purchases/');
  return response.data;
};

/**
 * Check if user has access to a specific item
 */
export const checkAccess = async (contentType, objectId) => {
  const response = await api.get('/api/payments/check-access/', {
    params: {
      content_type: contentType,
      object_id: objectId,
    },
  });
  return response.data;
};

export default {
  // Catalog
  fetchCatalog,
  fetchTracks,
  fetchTrackDetails,
  fetchCategories,
  fetchCategoryDetails,
  fetchTopicDetails,
  fetchProblemDetails,
  // Progress
  fetchUserProgress,
  fetchTopicProgress,
  markProblemComplete,
  // Access
  fetchUserAccess,
  checkTopicAccess,
  // Payments
  createPaymentOrder,
  verifyPayment,
  fetchUserPurchases,
  checkAccess,
};
